import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Scale, CheckCircle2, Loader2, X, Link2 } from "lucide-react";

const ROLES = [
  { k: "subject", l: "I am the subject of this record" },
  { k: "attorney", l: "I am the subject's attorney / representative" },
  { k: "witness", l: "I have corrected source information" },
  { k: "other", l: "Other" },
];

export default function TakedownForm({ abuserId, abuserName, onClose }) {
  const [role, setRole] = useState("subject");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 20) {
      toast.error("Please describe the issue in at least 20 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/takedowns", {
        abuser_id: abuserId,
        reason,
        requester_role: role,
        requester_name: name || undefined,
        requester_email: email || undefined,
        evidence_url: evidence || undefined,
      });
      setDone(true);
      toast.success("Request submitted. We'll respond within 5 business days.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Submission failed — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-zinc-900/70"
      onClick={onClose}
      data-testid="takedown-overlay"
    >
      <div
        className="bg-white border-2 border-zinc-900 w-full max-w-xl my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="takedown-dialog"
      >
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-start justify-between border-b border-red-600">
          <div>
            <div className="label-eyebrow text-red-500">Legal request</div>
            <div className="font-display font-black text-xl mt-1">
              Correction / Takedown
            </div>
            <div className="text-xs font-mono text-zinc-400 mt-1 truncate max-w-xs">
              Subject: {abuserName}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-300 hover:text-white"
            data-testid="takedown-close"
          >
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 bg-zinc-900 text-white mx-auto flex items-center justify-center border border-red-600">
              <CheckCircle2 size={28} />
            </div>
            <h2 className="font-display font-black text-2xl tracking-tighter mt-4">
              Request received
            </h2>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
              Your request has been logged and will be reviewed by the site
              operators. We aim to respond within 5 business days. If the record
              is removed during review it will immediately disappear from the
              public registry.
            </p>
            <button onClick={onClose} className="btn-outline mt-6" data-testid="takedown-ok">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4" data-testid="takedown-form">
            <div className="border border-zinc-300 bg-zinc-50 p-3 text-xs text-zinc-700 flex items-start gap-2">
              <Scale size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                Use this form to report an inaccuracy — the conviction was
                overturned, vacated, expunged or the person listed is not the
                subject of the source record. See our{" "}
                <a href="/legal#dmca" className="underline">
                  Legal page
                </a>{" "}
                for the full policy.
              </span>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">Your role *</label>
              <select
                className="field"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="takedown-role"
              >
                {ROLES.map((r) => (
                  <option key={r.k} value={r.k}>{r.l}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">
                Describe the inaccuracy *
              </label>
              <textarea
                rows={4}
                className="field"
                placeholder="What is wrong with this record and what should it say?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="takedown-reason"
              />
            </div>

            <div>
              <label className="label-eyebrow block mb-2">
                <Link2 size={12} className="inline mr-1" />
                Link to authoritative correction (optional)
              </label>
              <input
                className="field"
                placeholder="https://..."
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                data-testid="takedown-evidence"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className="field"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="takedown-name"
              />
              <input
                type="email"
                className="field"
                placeholder="Email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="takedown-email"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary disabled:opacity-60"
                data-testid="takedown-submit"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
              <button type="button" onClick={onClose} className="btn-outline" data-testid="takedown-cancel">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
